import { formatISO, isAfter, isValid, set } from 'date-fns';
import get from 'lodash/get';
import queryString from 'query-string';
import shortid from 'shortid';

import Skeleton from '@mui/material/Skeleton';

import { history } from '../../store';
import CellPersonWithImage from './CellPersonWithImage';

const reSplitAlphaNumeric = /([0-9]+)/gm;

// from TanStack table

function compareAlphanumeric(aStr, bStr) {
  // Split on number groups, but keep the delimiter
  // Then remove falsey split values
  const a = aStr?.split(reSplitAlphaNumeric)?.filter(Boolean);
  const b = bStr?.split(reSplitAlphaNumeric)?.filter(Boolean);

  // While
  while (a?.length && b?.length) {
    const aa = a.shift();
    const bb = b.shift();

    const an = parseInt(aa, 10);
    const bn = parseInt(bb, 10);

    const combo = [an, bn].sort();

    // Both are string
    if (Number.isNaN(combo[0])) {
      if (aa > bb) {
        return 1;
      }
      if (bb > aa) {
        return -1;
      }
    }

    // One is a string, one is a number
    if (Number.isNaN(combo[1])) {
      return Number.isNaN(an) ? -1 : 1;
    }

    // Both are numbers
    if (an > bn) {
      return 1;
    }
    if (bn > an) {
      return -1;
    }
  }

  return a.length - b.length;
}

const cleanValue = (value) => {
  if (typeof value === 'undefined') {
    return '';
  }
  if (value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.toLowerCase();
  }
  return value;
};

export const getCleanKey = (sortKey) => {
  let cleanKey = sortKey;
  if (sortKey?.charAt(0) === '-') {
    cleanKey = sortKey.substring(1);
  }
  return cleanKey;
};

export const getIsColumnSorted = (sortKey, id) => getCleanKey(sortKey) === id;

export const getSortDirection = (sortKey) =>
  sortKey?.charAt(0) === '-' ? 'desc' : 'asc';

export const getNewSortKey = (sortKey, id) => {
  if (getIsColumnSorted(sortKey, id)) {
    return sortKey?.charAt(0) === '-' ? sortKey.substring(1) : `-${sortKey}`;
  }
  return id;
};

export const sortEntriesByKey = (entries, columns, sortKey) => {
  if (!entries?.length) return [];
  if (!columns?.length) return [];
  if (!sortKey) return entries;

  const key = getCleanKey(sortKey);

  const sortedColumn = columns.find(({ id }) => id === key);

  if (!sortedColumn) return entries;

  const sortedEntries = entries.toSorted((a, b) => {
    const aValue = cleanValue(sortedColumn?.accessor(a));
    const bValue = cleanValue(sortedColumn?.accessor(b));

    if (sortKey?.charAt(0) === '-') {
      return compareAlphanumeric(bValue, aValue);
    }
    return compareAlphanumeric(aValue, bValue);
  });

  return sortedEntries;
};

export const sortEntries = (entries, sortKey, useRowData) => {
  if (!entries?.length) return [];
  if (!sortKey) return entries;

  let key = sortKey;
  let isReversed = false;
  if (typeof sortKey === 'string' && key.charAt(0) === '-') {
    key = sortKey.substring(1);
    isReversed = true;
  }
  let sortedEntries = entries.sort((a, b) => {
    let aVal = '';
    let bVal = '';

    // --- not used and probably won't
    if (typeof key === 'function' && useRowData) {
      aVal = key({ rowData: a });
      bVal = key({ rowData: b });
    } else if (typeof key === 'function' && !useRowData) {
      aVal = key(a);
      bVal = key(b);
      // ---
    } else {
      aVal = get(a, key);
      bVal = get(b, key);
    }
    aVal = cleanValue(aVal);
    bVal = cleanValue(bVal);
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
    }
    if (typeof bVal === 'string') {
      bVal = bVal.toLowerCase();
    }
    if (a === null) {
      return 1;
    }
    if (b === null) {
      return -1;
    }
    if (aVal < bVal) {
      return -1;
    }
    if (aVal > bVal) {
      return 1;
    }
    return 0;
  });
  if (isReversed) {
    sortedEntries = sortedEntries.reverse();
  }
  return sortedEntries;
};

export const filterEntries = (entries, keys, filter, useRowData) => {
  if (!filter || !entries || !entries.length) {
    return entries;
  }
  const formattedFilter = String(filter).toLowerCase();
  return entries.filter((entry) => {
    const doesMatch = keys.some((key) => {
      let val;
      if (typeof key === 'function' && useRowData) {
        val = key({ rowData: entry });
      } else if (typeof key === 'function' && !useRowData) {
        val = key(entry);
      } else {
        val = get(entry, key, '');
      }
      val = val === undefined ? '' : val;
      return String(val).toLowerCase().includes(formattedFilter);
    });
    if (doesMatch) {
      return true;
    }
    return false;
  });
};

export const defaultListParams = {
  limit: 25,
  offset: 0,
  building: null,
  q: null,
};

export const getInitialParams = (defaultParams) => {
  const parsed = queryString.parse(window.location.search);
  const params = { ...defaultParams, ...parsed };
  return params;
};

export const getDefaultParams = (customDefaultParams) => ({
  ...defaultListParams,
  ...customDefaultParams,
});

export const filterQueryParams = (obj) => {
  const newObj = { ...obj };
  delete newObj.limit;
  delete newObj.offset;
  Object.keys(newObj).forEach((key) => {
    const val = newObj[key];
    if (!val) {
      delete newObj[key];
    }
  });
  return newObj;
};

const getAltKeyValue = (e) => {
  e.stopPropagation();
  e.persist();
  return e.altKey;
};

export const handleOnClickProps = (rowInfo, onRowClick) => ({
  onClick: (e, handleOriginal) => {
    const isAltKey = getAltKeyValue(e);
    const target = e?.target;

    const isButton =
      target?.nodeName === 'BUTTON' ||
      target?.parentNode?.nodeName === 'BUTTON';
    const isLink = target?.nodeName === 'A';
    const isImg = target?.nodeName === 'IMG';
    const isMenu =
      target?.getAttribute('role') === 'presentation' ||
      target?.parentNode?.getAttribute('role') === 'presentation' ||
      target?.parentNode?.parentNode?.getAttribute('role') === 'presentation';
    const isExpander = target?.getAttribute('class')?.includes('expand');

    if (isButton || isLink || isImg || isMenu || isExpander) {
      if (handleOriginal) {
        handleOriginal();
      }
    } else {
      onRowClick && onRowClick(rowInfo, isAltKey);
    }
  },
});

export const handleStylesProps = (rowInfo, rowStyles = {}) => {
  const row = rowStyles ? { ...rowStyles } : {};
  if (rowInfo) {
    row.id = `table-item-${rowInfo.index}`;
  }
  return row;
};

export const getAddedByCell = (entry, user) => {
  let linkUrl = null;
  const creatorId = get(entry, 'creator.id', null);
  const userId = get(user, 'id', null);
  const buildingId = get(entry, 'building', null);
  if (buildingId && creatorId) {
    linkUrl = `/building/${entry.building}/staff/${entry.creator.id}`;
  }
  if (linkUrl && creatorId === userId) {
    linkUrl = '/global-person-search/profile';
  }
  const isDetailsAvailable = get(entry, 'creator.detailsAvailable', false);
  return (
    <CellPersonWithImage
      person={get(entry, 'creator')}
      linkUrl={isDetailsAvailable ? linkUrl : null}
      isAdminDefault
    />
  );
};

const table_config = 'table_config';

export const getDefaultColumnsWidth = (columns) => {
  if (!columns) return [];
  const defaultColumnsWidth = [];
  columns.forEach((column) => {
    const value = column.width;
    if (!value) return null;
    const id = column.id || column.accessor;
    if (value && id) {
      defaultColumnsWidth.push({
        id,
        value,
      });
    }
    return null;
  });
  return defaultColumnsWidth;
};

export const getColumnsWidth = (tableId, columns) => {
  if (!tableId || !columns) return null;
  const tableConfig = JSON.parse(localStorage.getItem(table_config));
  let columnsWidth = tableConfig?.columnsWidth?.[tableId];
  if (!columnsWidth) {
    columnsWidth = getDefaultColumnsWidth(columns);
  }
  return columnsWidth;
};

export const saveColumnsWidth = (tableId, columnsWidth) => {
  if (!tableId || !columnsWidth) return null;
  const tableConfig = JSON.parse(localStorage.getItem(table_config)) || {};
  if (!tableConfig?.columnsWidth) {
    tableConfig.columnsWidth = {};
  }
  tableConfig.columnsWidth[tableId] = columnsWidth;
  localStorage.setItem(table_config, JSON.stringify(tableConfig));
  return null;
};

export const resetColumnsWidth = (tableId) => {
  const tableConfig = JSON.parse(localStorage.getItem(table_config));
  if (!tableId || !tableConfig || !tableConfig[tableId]) return;
  tableConfig[tableId] = null;
  localStorage.setItem(table_config, JSON.stringify(tableConfig));
};

export const deleteTableConfig = () => {
  localStorage.removeItem(table_config);
};

export const getHiddenColumns = (tableId, columns) => {
  if (!tableId || !columns) return [];
  const tableConfig = JSON.parse(localStorage.getItem(table_config));
  const hiddenColumns = tableConfig?.hiddenColumns?.[tableId] || [];
  return hiddenColumns;
};

export const saveHiddenColumns = (tableId, hiddenColumns) => {
  if (!tableId || !hiddenColumns) return null;
  const tableConfig = JSON.parse(localStorage.getItem(table_config)) || {};
  if (!tableConfig?.hiddenColumns) {
    tableConfig.hiddenColumns = {};
  }
  tableConfig.hiddenColumns[tableId] = hiddenColumns;
  localStorage.setItem(table_config, JSON.stringify(tableConfig));
  return null;
};

export const formatActionItems = (items, isFullWidth) =>
  items?.map(({ url, onClick, ...rest }) => ({
    onClick: url ? () => history.push(url) : onClick,
    isFullWidth,
    ...rest,
  }));

export const getSkeletonColumns = (columns) => {
  if (!columns?.length) return [];
  return columns?.map(({ accessor, id, ...rest }) => ({
    ...rest,
    id: id ?? shortid.generate(),
    accessor: () => <Skeleton width="100%" />,
    Expander: () => <Skeleton width="100%" />,
  }));
};

export const getSkeletonEntries = (limit) => {
  const length = Number(limit) || 25;
  return [...Array(length).keys()];
};

const DEFAULT_START_TIME = { hours: 0, minutes: 0, seconds: 0 };
const DEFAULT_END_ANY_TIME = { hours: 23, minutes: 59, seconds: 59 };

export const getDateRangeFilterDate = (
  momentDate,
  isStartTime,
  useDefaultTime = true,
) => {
  if (!momentDate) return null;

  // transform a moment object into a date object
  const date = momentDate.toDate();

  // currently moment obj is returned
  // date should be a valid value after 1970
  if (!isValid(date) || !isAfter(date, new Date(1970))) return null;
  let formattedDate = date;
  if (useDefaultTime) {
    formattedDate = isStartTime
      ? set(date, DEFAULT_START_TIME)
      : set(date, DEFAULT_END_ANY_TIME);
  }
  return formatISO(formattedDate);
};

export const formatQueryParams = (params, buildingId) => {
  if (!Object.keys(params).length || !buildingId) return params;
  return { ...params, building: buildingId };
};

// when the row is expanded save its entry id to to able
// to set expanded:false if the entry is no longer present
export const getUpdatedExpandedRows = (expanded = {}, entries = []) => {
  const expandedState = { ...expanded };
  Object.keys(expanded).forEach((rowIndex) => {
    if (!expanded[rowIndex]) return;
    if (expanded[rowIndex]?.id) {
      const isExist = entries?.find(({ id }) => expanded[rowIndex]?.id === id);
      expandedState[rowIndex] = isExist ? { id: entries[rowIndex]?.id } : false;
    } else {
      expandedState[rowIndex] = { id: entries[rowIndex]?.id };
    }
  });
  return expandedState;
};
